import { PLACES, formatPlace, searchPlaces, type Place } from '../../astro/places.ts';
import { hasHistoricalTzData, type ResolvedTime } from '../../astro/time.ts';
import type { WallClock } from '../../astro/time.ts';
import { PRECISION_LABELS, type TimePrecision } from '../../astro/uncertainty.ts';

/**
 * The shared birth-details fieldset: name, date, time, precision, and place.
 *
 * The precision control is not optional politeness — it drives the uncertainty
 * gate that decides what the tools are willing to assert. A form that quietly
 * assumes an exact time is how most free chart sites end up confidently wrong.
 */

export interface BirthDetails {
  name: string;
  wall: WallClock;
  precision: TimePrecision;
  latitude: number;
  longitude: number;
  timezone: string;
  placeLabel: string;
  /** False when the caller allowed a missing place — see readBirthDetails. */
  hasPlace: boolean;
}

export interface ReadOptions {
  /**
   * Allow submission without a birth place.
   *
   * Only the Lucky Colour Finder's date-only mode uses this: the weekday lord
   * needs no coordinates. A chart or a kuta score always needs a place, and
   * must never be computed from a default one.
   */
  placeOptional?: boolean;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K, className?: string, text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
}

/** Wires the place input's autocomplete and manual-coordinate fallback. */
export function initPlaceInput(form: HTMLFormElement): void {
  const input = form.querySelector<HTMLInputElement>('[data-place-input]');
  const list = form.querySelector<HTMLElement>('[data-place-results]');
  const manual = form.querySelector<HTMLElement>('[data-place-manual]');
  const toggle = form.querySelector<HTMLElement>('[data-place-manual-toggle]');
  if (!input || !list) return;

  const select = (place: Place) => {
    input.value = formatPlace(place);
    input.dataset.lat = String(place.latitude);
    input.dataset.lon = String(place.longitude);
    input.dataset.tz = place.timezone;
    list.replaceChildren();
    list.hidden = true;
  };

  input.addEventListener('input', () => {
    delete input.dataset.lat; // a typed edit invalidates the previous pick
    const results = searchPlaces(input.value);
    list.replaceChildren();
    if (results.length === 0) {
      list.hidden = true;
      return;
    }
    for (const place of results) {
      const btn = el('button', 'place-result', formatPlace(place));
      btn.type = 'button';
      btn.addEventListener('click', () => select(place));
      list.appendChild(btn);
    }
    list.hidden = false;
  });

  input.addEventListener('blur', () => {
    // Delay so a click on a result still registers.
    window.setTimeout(() => { list.hidden = true; }, 150);
  });

  if (toggle && manual) {
    toggle.addEventListener('click', () => {
      manual.hidden = !manual.hidden;
    });
  }
}

/** Renders the ICU warning when the runtime lacks historical timezone rules. */
export function checkTimezoneSupport(form: HTMLFormElement): void {
  if (hasHistoricalTzData()) return;
  const manual = form.querySelector<HTMLElement>('[data-place-manual]');
  if (manual) manual.hidden = false;
  const warn = el('p', 'tool-caveat',
    'Your browser does not carry historical timezone rules, so we cannot work out the ' +
    'correct UTC offset for older birth dates automatically. Please enter the offset manually.');
  form.prepend(warn);
}

/**
 * Reads and validates the fieldset. Returns null and marks the offending field
 * when something is missing, rather than computing from a partial form.
 */
export function readBirthDetails(
  form: HTMLFormElement,
  options: ReadOptions = {},
): BirthDetails | null {
  const get = (name: string) => form.querySelector<HTMLInputElement | HTMLSelectElement>(`[name="${name}"]`);

  const dateInput = get('date') as HTMLInputElement | null;
  const timeInput = get('time') as HTMLInputElement | null;
  const precisionInput = get('precision') as HTMLSelectElement | null;
  const placeInput = form.querySelector<HTMLInputElement>('[data-place-input]');
  const nameInput = get('name') as HTMLInputElement | null;

  if (!dateInput || !precisionInput || !placeInput) return null;

  const invalid = (field: HTMLElement | null, message: string) => {
    if (field) field.setAttribute('aria-invalid', 'true');
    const existing = form.querySelector('.tool-form__error');
    if (existing) existing.remove();
    const err = el('p', 'tool-form__error', message);
    err.setAttribute('role', 'alert');
    form.appendChild(err);
    return null;
  };

  form.querySelectorAll('[aria-invalid]').forEach((n) => n.removeAttribute('aria-invalid'));
  form.querySelector('.tool-form__error')?.remove();

  if (!dateInput.value) return invalid(dateInput, 'Please enter a date of birth.');

  const [y, m, d] = dateInput.value.split('-').map(Number);
  if (!y || !m || !d) return invalid(dateInput, 'That date of birth is not valid.');

  const precision = precisionInput.value as TimePrecision;

  let hour = 12;
  let minute = 0;
  if (precision !== 'unknown') {
    if (!timeInput?.value) {
      return invalid(timeInput, 'Please enter a birth time, or choose "I don\'t know the birth time".');
    }
    const [hh, mm] = timeInput.value.split(':').map(Number);
    hour = hh ?? 0;
    minute = mm ?? 0;
  }

  // Coordinates: either a picked place, or explicit manual entry.
  let latitude: number;
  let longitude: number;
  let timezone: string;
  let placeLabel: string;

  const manualLat = get('manual-lat') as HTMLInputElement | null;
  const manualLon = get('manual-lon') as HTMLInputElement | null;

  if (placeInput.dataset.lat && placeInput.dataset.lon) {
    latitude = Number(placeInput.dataset.lat);
    longitude = Number(placeInput.dataset.lon);
    timezone = placeInput.dataset.tz ?? 'Asia/Kolkata';
    placeLabel = placeInput.value;
  } else if (manualLat?.value && manualLon?.value) {
    latitude = Number(manualLat.value);
    longitude = Number(manualLon.value);
    timezone = (get('manual-tz') as HTMLSelectElement | null)?.value || 'Asia/Kolkata';
    placeLabel = `${latitude.toFixed(2)}, ${longitude.toFixed(2)}`;
    if (!Number.isFinite(latitude) || Math.abs(latitude) > 90) {
      return invalid(manualLat, 'Latitude must be between -90 and 90.');
    }
    if (!Number.isFinite(longitude) || Math.abs(longitude) > 180) {
      return invalid(manualLon, 'Longitude must be between -180 and 180.');
    }
  } else if (options.placeOptional) {
    /* No coordinates, and the caller said that's acceptable. Anything needing
       a location must check `hasPlace` rather than trusting these zeros. */
    latitude = 0;
    longitude = 0;
    timezone = 'Asia/Kolkata';
    placeLabel = '';
    return {
      name: nameInput?.value.trim() ?? '',
      wall: { year: y, month: m, day: d, hour, minute },
      precision,
      latitude,
      longitude,
      timezone,
      placeLabel,
      hasPlace: false,
    };
  } else {
    return invalid(
      placeInput,
      'Please choose a birth place from the list, or enter coordinates manually.',
    );
  }

  return {
    name: nameInput?.value.trim() ?? '',
    wall: { year: y, month: m, day: d, hour, minute },
    precision,
    latitude,
    longitude,
    timezone,
    placeLabel,
    hasPlace: true,
  };
}

/**
 * Renders an unresolvable birth time as an explanation and a question — never
 * as a chart computed from a guess.
 */
export function renderTimeProblem(resolved: Extract<ResolvedTime, { ok: false }>): HTMLElement {
  const box = el('div', 'tool-caveat tool-caveat--block');
  box.appendChild(el('h4', 'tool-caveat__title', "We can't place this birth time yet"));
  box.appendChild(el('p', undefined, resolved.message));

  if (resolved.reason === 'ambiguous') {
    const list = el('ul', 'tool-caveat__options');
    for (const cand of resolved.candidates) {
      const hours = cand.offsetMinutes / 60;
      const sign = hours >= 0 ? '+' : '−';
      const abs = Math.abs(hours);
      list.appendChild(el('li', undefined,
        `UTC${sign}${String(Math.floor(abs)).padStart(2, '0')}:${String(Math.round((abs % 1) * 60)).padStart(2, '0')}`));
    }
    box.appendChild(list);
    box.appendChild(el('p', undefined,
      'Enter the offset that applied using the manual coordinates option above.'));
  }

  if (resolved.reason === 'lmt' && resolved.suggestedLmtOffsetMinutes !== undefined) {
    const mins = resolved.suggestedLmtOffsetMinutes;
    const h = Math.floor(Math.abs(mins) / 60);
    const m = Math.round(Math.abs(mins) % 60);
    box.appendChild(el('p', undefined,
      `Local mean time for the longitude you gave would be about UTC${mins >= 0 ? '+' : '−'}` +
      `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}.`));
  }

  return box;
}

export { PLACES };
